import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Contrôle du visage réservé aux ANNONCEURS (jamais aux visiteurs qui consultent).
 *  Un selfie est comparé à la première photo de l'annonce : si c'est la même personne,
 *  le profil obtient le badge « Profil certifié » et la publication se poursuit. */
export const VerificationFaciale = ({ profileId, categorie, photoReference, onCertifie, onFerme }) => {
  const video = useRef(null);
  const flux = useRef(null);
  const fichier = useRef(null);
  const [cameraPrete, setCameraPrete] = useState(false);
  const [analyse, setAnalyse] = useState(false);
  const [echec, setEchec] = useState(null);

  useEffect(() => {
    let actif = true;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((s) => {
        if (!actif) { s.getTracks().forEach((t) => t.stop()); return; }
        flux.current = s;
        if (video.current) video.current.srcObject = s;
        setCameraPrete(true);
      })
      .catch(() => setCameraPrete(false));
    return () => {
      actif = false;
      flux.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const envoyer = async (blob) => {
    setAnalyse(true);
    setEchec(null);
    const form = new FormData();
    form.append("profile_id", profileId);
    form.append("categorie", categorie);
    form.append("photo_reference", photoReference || "");
    form.append("selfie", blob, "selfie.jpg");
    try {
      const r = await axios.post(`${API}/verification-faciale/soumettre`, form);
      if (r.data.ok) {
        toast.success("Profil certifié !", { description: "Votre badge est actif sur vos annonces." });
        onCertifie?.(r.data);
        return;
      }
      if (r.data.contourne) {
        toast.message("Contrôle indisponible", { description: r.data.message });
        onCertifie?.(r.data);
        return;
      }
      setEchec(r.data.message);
    } catch (e) {
      const d = e.response?.data?.detail;
      setEchec(typeof d === "string" ? d : d?.message || "Contrôle impossible. Réessayez.");
    } finally {
      setAnalyse(false);
    }
  };

  const capturer = () => {
    const v = video.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => b && envoyer(b), "image/jpeg", 0.9);
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
      data-testid="verification-faciale">
      <div className="w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        <header className="flex items-start justify-between gap-3 bg-[#0A5C36] px-6 py-5 text-white">
          <div>
            <p className="font-heading text-xl font-extrabold">Contrôle du visage</p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-white/75">
              <ShieldCheck size={12} /> Réservé aux annonceurs · badge « Profil certifié »
            </p>
          </div>
          <button onClick={onFerme} data-testid="verification-faciale-fermer"
            className="rounded-full p-2 transition-colors hover:bg-white/10"><X size={18} /></button>
        </header>

        <div className="space-y-4 px-6 py-6">
          <p className="text-sm leading-relaxed text-stone-600">
            Cette rubrique dépasse 200 annonces : pour protéger les clients, nous comparons un
            selfie à la photo de votre annonce. Les visiteurs qui consultent les annonces ne sont
            <strong> jamais </strong>concernés par ce contrôle.
          </p>

          <div className="relative overflow-hidden rounded-2xl bg-stone-900 aspect-[4/3]">
            <video ref={video} autoPlay playsInline muted data-testid="verification-faciale-video"
              className="h-full w-full object-cover" />
            {!cameraPrete && (
              <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-xs text-white/70"
                data-testid="verification-faciale-sans-camera">
                Caméra indisponible : envoyez une photo de vous prise à l'instant.
              </p>
            )}
          </div>

          {echec && (
            <p className="rounded-xl bg-red-50 px-4 py-3 text-xs font-semibold text-red-700"
              data-testid="verification-faciale-echec">{echec}</p>
          )}

          <div className="flex flex-col gap-2">
            {cameraPrete && (
              <button onClick={capturer} disabled={analyse} data-testid="verification-faciale-capturer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A5C36] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#084027] disabled:opacity-60">
                {analyse ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                {analyse ? "Analyse en cours…" : "Prendre le selfie et valider"}
              </button>
            )}
            <input ref={fichier} type="file" accept="image/*" capture="user" className="hidden"
              data-testid="verification-faciale-fichier"
              onChange={(e) => e.target.files?.[0] && envoyer(e.target.files[0])} />
            <button onClick={() => fichier.current?.click()} disabled={analyse}
              data-testid="verification-faciale-importer"
              className="rounded-full border border-[#E5DFD5] bg-white px-6 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-60">
              Envoyer une photo depuis mon téléphone
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
